<template>
	<div
		class="gallery-modal__sidebar"
	>
		<div
			class="gallery-modal__sidebar-content"
			:class="{ 'gallery-modal__sidebar-content--loading': loading }"
		>
			<div class="gallery-modal__sidebar-heading">
				<h4 class="gallery-modal__sidebar-sub-title">
					Tags
				</h4>
			</div>
			<div class="gallery-modal__sidebar-block gallery-modal__sidebar-block--separator">
				<TransitionGroup
					name="grid"
					tag="ul"
					class="tags tags--inverted "
				>
					<li
						v-for="tag in selected_option.database_tags"
						:key="tag.id"
						class="tags__tag tags__tag--selected"
					>
						{{ tag.title }}
						<button
							class="tags__remove-tag"
							@click="removeTag( tag.id )"
						/>
					</li>
				</TransitionGroup>
			</div>
			<div class="gallery-modal__comments-heading">
				<h4 class="gallery-modal__sidebar-sub-title">
					Add tags
				</h4>
				<button
					v-if="!show_create_tags"
					@click="show_create_tags = !show_create_tags"
					class="gallery-modal__create-tag-btn"
				>
					Create tag
				</button>
			</div>
			<div
				v-if="show_create_tags"
				class="gallery-modal__sidebar-block gallery-modal__sidebar-block--separator"
			>
				<div class="form__set form__set--sidebar mb-16">
					<label
						for="new_tag"
						class="form__label"
					>
						Enter a tag
					</label>
					<input
						id="new_tag"
						ref="new_tag_input"
						v-model="new_tag"
						class="form__input form__input--lowercase"
						type="text"
						name="new_tag"
						:disabled="loading"
						@keyup.enter="submitNewTag"
					>
				</div>

				<button
					class="button button--invert"
					:class="{ 'button--loading': loading }"
					:disabled="!new_tag || loading"
					@click="submitNewTag"
				>
					Create tag
				</button>

				<p
					v-if="error_message"
					class="error-message"
				>
					{{ error_message }}
				</p>
			</div>
			<div class="gallery-modal__sidebar-block">
				<div class="search-bar search-bar--sidebar mb-16">
					<div
						class="search-bar__field a4-12"
						@click="focusTagSearch"
					>
						<input
							ref="tag_search_input"
							class="search__input search__input--fw-400"
							type="text"
							placeholder="Search for a tag"
							@input="handleTagSearchInputChange"
						>
					</div>
				</div>
				<div
					ref="tags_container"
					class="tags-container tags-container--sidebar"
				>
					<TransitionGroup
						v-if="filtered_common_tags.length"
						name="grid"
						tag="ul"
						class="tags tags--search tags--overflow-scroll"
						:class="{
							'tags--overflowing': tags_overflowing
						}"
					>
						<li
							v-for="tag in filtered_common_tags"
							:key="tag.id"
							class="tags__tag tags__tag--selectable"
							@click="addTag( tag.title )"
						>
							{{ tag.title }}
						</li>
					</TransitionGroup>
					<p
						v-else
						class="mb-32"
					>
						No common tags found.
					</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>

import { computed, ref, onMounted } from 'vue';
import { useDataStore } from '../../stores/data';

const data_store = useDataStore();
getDatabaseTagsBySearch

const {
	getCommonDatabaseTags,
	getDatabaseTagsByTitle,
	saveDatabaseTag,
	saveTagsToOption,
	getDatabaseTagsBySearch
} = data_store;

const new_tag = ref( '' );
const common_tags = ref( [] );
const tags_have_changed = ref( false );
const loading = ref( false );
const show_create_tags = ref( false );
const error_message = ref( null );
const project = ref( null );
const tags_overflowing = ref( false );

const tags_container = ref( null ); // Ref to container element
const new_tag_input = ref( null ); // Ref to container element
const tag_search_input = ref( null ); // Ref to container element

const props = defineProps( {
	selectedOption: {
		required: true,
		type: Object
	},
} );

const emit = defineEmits( [ 'update-database-tags' ] );

const selected_option = computed( () => {
	return props.selectedOption;
} );

project.value = selected_option.value.related_project[0];

const base_asset_type = computed( () => {
	if ( !selected_option.value.media_type || Object.keys( selected_option.value.media_type ).length === 0 ) {
		return '';
	}

	return selected_option.value.media_type.base_type;
} );

const filtered_project_tags = computed( () => {
	return project.value.project_tags.filter( tag => {
		const option_tags = selected_option.value.database_tags || [];
		const option_tag_titles = option_tags.map( option_tag => option_tag.title.toLowerCase() );
		return !option_tag_titles.includes( tag.title.toLowerCase() );
	} );
} );

const filtered_common_tags = computed( () => {
	return common_tags.value.filter( tag => {
		const option_tags = selected_option.value.database_tags || [];
		const option_tag_titles = option_tags.map( option_tag => option_tag.title.toLowerCase() );
		return !option_tag_titles.includes( tag.title.toLowerCase() );
	} );
} );

onMounted( async() => {
	await addAllProjectTags();

	loading.value = true;

	try {
		const response = await getCommonDatabaseTags();
		if ( response ) {
			common_tags.value = response;
		}
	} catch ( error ) {
		// console.log( error );
	}
	loading.value = false;
	tags_list_resize_observer.observe( tags_container.value );
} );

async function addTag( title ) {
	error_message.value = null;
	loading.value = true;

	let matching_tag = null;

	try {
		const response = await getDatabaseTagsByTitle( title );
		if ( response.data.tags && response.data.tags.length ) {
			matching_tag = response.data.tags.find( tag => tag.title.toLowerCase() === title.toLowerCase() );
		}
	} catch ( error ) {
		// error_message.value = error;
	}

	if ( !matching_tag ) {
		try {
			const response = await saveDatabaseTag( title );
			matching_tag = response.data.save_database_tags_Tag;
		} catch ( error ) {
			error_message.value = error;
			loading.value = false;
			return;
		}
	}

	try {
		if ( !matching_tag ) {
			error_message.value = 'Something went wrong';
			loading.value = false;
			return;
		}

		if ( !base_asset_type ) {
			error_message.value = `Invalid base_asset_type: ${base_asset_type.value}`;
		}

		const tag_id = matching_tag.id;
		const tags = [
			...selected_option.value.database_tags.map( tag => parseInt( tag.id ) ),
			parseInt( tag_id )
		];

		const params = { option_id: selected_option.value.id, tags: tags, asset_type: base_asset_type.value };
		const option_response = await saveTagsToOption( params );
		const option_entry = option_response.data && option_response.data[`save_revision_options_${base_asset_type.value}_Entry`];

		if ( !option_entry ) {
			error_message.value = 'Failed to save option';
		}

		selected_option.value.database_tags = option_entry.database_tags;
		tags_have_changed.value = true;
		emit( 'update-database-tags', option_entry, selected_option.value.id );
	} catch ( error ) {
		error_message.value = error;
		loading.value = false;
		return;
	}

	loading.value = false;

}

async function addAllProjectTags() {
	loading.value = true;
	error_message.value = null;
	const tags_to_add = ref( [] );

	for ( let i = 0; i < filtered_project_tags.value.length; i++ ) {
		let matching_tag = null;

		try {
			const response = await getDatabaseTagsByTitle( filtered_project_tags.value[i].title );
			if ( response.data.tags && response.data.tags.length ) {
				matching_tag = response.data.tags.find( tag => tag.title.toLowerCase() === filtered_project_tags.value[i].title.toLowerCase() );
			}
		} catch ( error ) {
			// error_message.value = error;
		}

		if ( !matching_tag ) {
			try {
				const response = await saveDatabaseTag( filtered_project_tags.value[i].title );
				matching_tag = response.data.save_database_tags_Tag;
			} catch ( error ) {
				error_message.value = error;
				if ( i === filtered_project_tags.value.length - 1 ) {
					loading.value = false;
				}
				continue;
			}
		}

		if ( !matching_tag ) {
			error_message.value = 'Something went wrong';
			if ( i === filtered_project_tags.value.length - 1 ) {
				loading.value = false;
			}
			continue;
		}
		tags_to_add.value.push( matching_tag );
	}

	try {
		if ( !base_asset_type ) {
			error_message.value = `Invalid base_asset_type: ${base_asset_type.value}`;
		}

		const option_tags = selected_option.value.database_tags || [];
		const tags = [
			...option_tags.map( tag => parseInt( tag.id ) ),
			...tags_to_add.value.map( tag => parseInt( tag.id ) )
		];

		const params = { option_id: selected_option.value.id, tags: tags, asset_type: base_asset_type.value };
		const option_response = await saveTagsToOption( params );
		const option_entry = option_response.data && option_response.data[`save_revision_options_${base_asset_type.value}_Entry`];

		if ( !option_entry ) {
			error_message.value = 'Failed to save option';
		}

		selected_option.value.database_tags = option_entry.database_tags;
		tags_have_changed.value = true;
	} catch ( error ) {
		error_message.value = error;
		loading.value = false;
	}

	loading.value = false;
}

async function removeTag( tag_id ) {
	loading.value = true;
	try {
		if ( !base_asset_type ) {
			error_message.value = `Invalid base_asset_type: ${base_asset_type.value}`;
		}

		const option_tags = selected_option.value.database_tags || [];
		const tags = [
			...option_tags
				.filter( tag => tag.id !== tag_id )
				.map( tag => parseInt( tag.id ) )
		];

		const params = { option_id: selected_option.value.id, tags: tags, asset_type: base_asset_type.value };
		const option_response = await saveTagsToOption( params );
		const option_entry = option_response.data && option_response.data[`save_revision_options_${base_asset_type.value}_Entry`];

		if ( !option_entry ) {
			error_message.value = 'Failed to save option';
		}

		selected_option.value.database_tags = option_entry.database_tags;
		tags_have_changed.value = true;
		emit( 'update-database-tags', option_entry, selected_option.value.id );

	} catch ( error ) {
		error_message.value = error;
		loading.value = false;
		return;
	}

	loading.value = false;
}

async function submitNewTag() {
	await addTag( new_tag.value );
	new_tag.value = '';
	new_tag_input.value.focus();
}

// Check tags list overflow
const tags_list_resize_observer = new ResizeObserver( entries => {
	entries.forEach( entry => {
		const tags_list = entry.target.firstChild ?? null;
		if ( !tags_list ) {
			return;
		}

		tags_overflowing.value = tags_list.scrollHeight > tags_list.clientHeight || tags_list.scrollWidth > tags_list.clientWidth;
	} );
} );

const handleTagSearchInputChange = async( e ) => {
	const input = e.target.value;
	setTimeout( async() => {
		error_message.value = null;
		loading.value = true;

		try {
			const response = await getDatabaseTagsBySearch( input );
			if ( response.data.tags && response.data.tags.length ) {
				common_tags.value = response.data.tags;
			}
		} catch ( error ) {
			error_message.value = error;
		}

		loading.value = false;
	}, 500 );
};

const focusTagSearch = () => {
	tag_search_input.value.focus();
};

</script>
